// ALGORITMO SEMBRADO

// algoritmo de sembrado de regiones

import axios from "axios";
import { toast } from "react-hot-toast";
import API_BASE_URL from "../config/config.js";

export const randomSembradoRegion = async (
  idevent,
  idsport,
  regions,
  groupSizes // Ahora recibimos groupSizes en lugar de groupCount
) => {
  try {
    const ideventValue = Number(idevent);
    const idsportValue = Number(idsport);

    // Clonar las regiones para preservar los valores originales de `total`
    const originalRegions = JSON.parse(JSON.stringify(regions));

    // Clonar los parámetros originales para usarlos en caso de reintento
    const originalParams = {
      idevent,
      idsport,
      regions: originalRegions,
      groupSizes, // Usar groupSizes en lugar de groupCount
    };

    // Inicializar los grupos con el tamaño especificado
    const groups = groupSizes.map((size, index) => ({
      idgroup: index + 1,
      participants: [],
      maxParticipants: size, // Asignar el tamaño máximo de participantes
      countPerRegion: {
        Lima: 0,
        Sur: 0,
        Norte: 0,
        Centro: 0,
      },
    }));

    // Calcular el número total de participantes
    const totalParticipants = regions.reduce(
      (sum, region) => sum + region.total,
      0
    );

    // Función para asignar un participante asegurando que cada grupo tenga un nro_grupo único
    const assignParticipantToGroup = (region, group, nro_grupo) => {
      if (group.participants.length >= group.maxParticipants) return; // Verificar límite de participantes
      group.participants.push({
        idevent: ideventValue,
        idsport: idsportValue,
        idgroup: group.idgroup,
        nro_grupo, // Asegurar que sea único dentro del grupo
        idregion: region.idregion,
      });
      group.countPerRegion[region.name]++;
      region.total--; // Reducir el total de participantes restantes en esa región
    };

    // Asignar las cabezas de grupo de acuerdo con la especificación de 'heads'
    const assignHeadsToGroups = () => {
      regions.forEach((region) => {
        for (let i = 0; i < region.heads; i++) {
          const availableGroups = groups.filter(
            (group) => group.participants.length === 0 // Asegurar que solo se asignen a grupos vacíos en la posición 1
          );
          if (availableGroups.length > 0) {
            const randomGroup =
              availableGroups[
                Math.floor(Math.random() * availableGroups.length)
              ];
            assignParticipantToGroup(region, randomGroup, 1); // Cabezas en nro_grupo 1
          }
        }
      });
    };

    // Asignar las cabezas de grupo
    assignHeadsToGroups();

    // Función para distribuir el resto de los participantes secuencialmente por nro_grupo
    const distributeParticipantsSequentially = (nro_grupo) => {
      regions.forEach((region) => {
        const maxPerGroup = region.total >= groups.length ? 2 : 1; // Asegurar máximo de 2 si hay más participantes que grupos

        while (region.total > 0) {
          const availableGroups = groups.filter(
            (group) =>
              group.countPerRegion[region.name] < maxPerGroup && // No superar el máximo de 2 por región
              group.participants.length < group.maxParticipants && // Respetar el límite de participantes del grupo
              !group.participants.some((p) => p.nro_grupo === nro_grupo) // Asegurar que el nro_grupo esté disponible en el grupo
          );

          if (availableGroups.length === 0) break; // Si no hay más grupos disponibles, detener

          const randomGroup =
            availableGroups[Math.floor(Math.random() * availableGroups.length)];
          assignParticipantToGroup(region, randomGroup, nro_grupo); // Asignar en el nro_grupo actual
        }
      });
    };

    // Distribuir los participantes por cada nro_grupo secuencialmente
    for (let i = 2; i <= 4; i++) {
      distributeParticipantsSequentially(i);
    }

    // Función de respaldo para asignar cualquier participante restante
    const distributeRemainingParticipants = () => {
      regions.forEach((region) => {
        while (region.total > 0) {
          const availableGroups = groups.filter(
            (group) =>
              group.participants.length < group.maxParticipants && // Respetar el límite de participantes del grupo
              group.countPerRegion[region.name] < 2 // No exceder el máximo de 2 por región
          );

          if (availableGroups.length === 0) {
            console.error(
              "Error: No hay grupos disponibles para asignar participantes restantes."
            );
            break;
          }

          const randomGroup =
            availableGroups[Math.floor(Math.random() * availableGroups.length)];
          assignParticipantToGroup(
            region,
            randomGroup,
            randomGroup.participants.length + 1 // Asignar en la siguiente posición disponible
          );
        }
      });
    };

    // Distribuir los participantes restantes
    distributeRemainingParticipants();

    // Verificar si queda algún participante sin asignar
    const allParticipantsAssigned = regions.every(
      (region) => region.total === 0
    );

    if (!allParticipantsAssigned) {
      console.error(
        "Error: La distribución no generó el número correcto de participantes."
      );
      return;
    }

    // Construir el arreglo final para enviar al backend
    const groupSembrado = groups.flatMap((group) => group.participants);

    // Verificar que el total de elementos sea correcto
    if (groupSembrado.length !== totalParticipants) {
      console.error(
        "Error: La distribución no generó el número correcto de participantes."
      );
      return;
    }

    // **Análisis final para verificar la distribución de regiones en los grupos**
    const analyzeFinalDistribution = (groups, originalRegions) => {
      const distributionProblems = [];

      groups.forEach((group) => {
        const regionCount = {};

        // Contar el número de participantes de cada región en el grupo
        group.participants.forEach((participant) => {
          const regionName = originalRegions.find(
            (region) => region.idregion === participant.idregion
          )?.name;
          if (regionName) {
            regionCount[regionName] = (regionCount[regionName] || 0) + 1;
          }
        });

        // Verificar si alguna región excede el número permitido por grupo
        Object.keys(regionCount).forEach((regionName) => {
          const regionTotal = originalRegions.find(
            (r) => r.name === regionName
          ).total;

          // Si el número total de participantes de una región es menor o igual al número de grupos
          if (regionTotal <= groups.length) {
            if (regionCount[regionName] > 1) {
              distributionProblems.push(
                `Problema detectado: ${regionName} aparece ${regionCount[regionName]} veces en el Grupo ${group.idgroup}.`
              );
            }
          } else {
            // Si hay más participantes de una región que grupos, calcular el número máximo permitido en un grupo
            const expectedMax = Math.ceil(regionTotal / groups.length);
            if (regionCount[regionName] > expectedMax) {
              distributionProblems.push(
                `Problema detectado: ${regionName} aparece ${regionCount[regionName]} veces en el Grupo ${group.idgroup}.`
              );
            }
          }
        });
      });

      if (distributionProblems.length > 0) {
        console.log("Análisis Final de la Distribución:", distributionProblems);
      } else {
        console.log(
          "Análisis Final de la Distribución: Sin problemas detectados."
        );
      }

      return distributionProblems;
    };

    // Realizar el análisis final
    const distributionProblems = analyzeFinalDistribution(
      groups,
      originalRegions
    );

    // Si se detectan problemas, llamar a la función de nuevo con los parámetros originales
    if (distributionProblems.length > 0) {
      console.log("Detectando problemas, volviendo a ejecutar...");
      return randomSembradoRegion(
        originalParams.idevent,
        originalParams.idsport,
        JSON.parse(JSON.stringify(originalParams.regions)), // Clon para evitar modificar los datos originales
        originalParams.groupSizes // Usar groupSizes en lugar de groupCount
      );
    }

    // Enviar los datos al endpoint
    console.log("Información del Sembrado", groupSembrado);
    await axios.post(`${API_BASE_URL}/postsembrado`, groupSembrado);

    toast.success("¡Sembrado generado exitosamente!");
  } catch (error) {
    toast.error("¡Error al generar sembrado!");
    console.error("Error al generar sembrado", error);
  }
};
